import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// No fallback images - carousel will be empty until images are uploaded
const defaultSlides = [];

const InstagramCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [slides, setSlides] = useState(defaultSlides);
  const [speed, setSpeed] = useState(4000); // Default 4 seconds
  const [failedImages, setFailedImages] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle image load error - track failed images
  const handleImageError = (slideId) => {
    setFailedImages(prev => new Set([...prev, slideId]));
  };

  // Fetch carousel images and settings from database
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await axios.get(`${API}/api/carousel/images`);
        if (response.data.images && response.data.images.length > 0) {
          const API_URL = process.env.REACT_APP_BACKEND_URL || '';
          const dbSlides = response.data.images.map((img, index) => ({
            id: img._id,
            image: img.url.startsWith('http') ? img.url : `${API_URL}${img.url}`,
            caption: img.title || 'Mini Stripes UK',
            alt: img.title || 'Mini Stripes Product Photo'
          }));
          setSlides(dbSlides);
        }
        // Load speed setting
        if (response.data.speed) {
          setSpeed(response.data.speed);
        }
      } catch (error) {
        console.log('Using default carousel images');
      }
    };
    fetchImages();
  }, []);

  // 2 images on mobile, 3 on desktop
  const slidesPerView = isMobile ? 2 : 3;
  const maxIndex = Math.max(0, slides.length - slidesPerView);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => prev >= maxIndex ? 0 : prev + 1);
  }, [maxIndex]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => prev <= 0 ? maxIndex : prev - 1);
  }, [maxIndex]);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || slides.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => prev >= maxIndex ? 0 : prev + 1);
    }, speed);

    return () => clearInterval(interval);
  }, [isAutoPlaying, slides.length, speed, maxIndex]);

  // Pause autoplay on hover
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  if (slides.length === 0) return null;

  return (
    <div className="w-full" data-testid="instagram-carousel-section">
      {/* Carousel Container */}
      <div 
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
          {/* Main Carousel - Show 2 images at a time on desktop */}
          <div className="overflow-hidden rounded-2xl shadow-2xl">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * (100 / slidesPerView)}%)` }}
            >
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className="relative flex-shrink-0 w-1/2 md:w-1/3 px-1"
                  data-testid={`carousel-slide-${index}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-800 rounded-lg">
                    {!failedImages.has(slide.id) ? (
                      <img
                        src={slide.image}
                        alt={slide.alt}
                        className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                        loading={index === 0 ? 'eager' : 'lazy'}
                        onError={() => handleImageError(slide.id)}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                        <span className="text-lg">Image unavailable</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
    </div>
  );
};

export default InstagramCarousel;
